import React, {useCallback, useContext, useState} from 'react';
import {AppContext} from '../context/AppContext';
import ConfirmDialog from './ConfirmDialog';

const SettingsView: React.FC = () => {
  const context = useContext(AppContext);

  if (!context) {
    throw new Error('SettingsView must be used within AppContextProvider');
  }

  const {
    i18n,
    language,
    setLanguage,
    // autoSync,
    // setAutoSync,
    theme,
    setTheme,
    signOut,
    exportAll,
    importAll
  } = context;

  // ConfirmDialog state and handlers
  const [showConfirmSignOut, setShowConfirmSignOut] = useState(false);

  const handleSignOutClick = useCallback(() => {
    setShowConfirmSignOut(true);
  }, []);

  const handleConfirmSignOut = useCallback(async () => {
    try {
      await signOut();
    } finally {
      setShowConfirmSignOut(false);
    }
  }, [signOut]);

  const handleCancelSignOut = useCallback(() => {
    setShowConfirmSignOut(false);
  }, []);

  const handleExportJson = async () => {
    try {
      const data = await exportAll();
      const blob = new Blob([data], {type: 'application/json'});
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `falcon-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const handleImportJson = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        const text = await file.text();
        await importAll(text);
        alert('Data imported successfully');
      } catch (error) {
        console.error('Import failed:', error);
        alert('Failed to import data. Please check the console for details.');
      }
      // Reset file input
      event.target.value = '';
    }
  };

  return (
    <section id="settings-view" className="view">
      <div className="row">
        <div className="card settings-generic">
          <h2>{i18n.t('generalSettings')}</h2>
          <div className="">
            <div className="field">
              <label>
                <span>{i18n.t('language')}</span>
                <select
                  id="language"
                  value={language}
                  onChange={(e) => {
                    setLanguage(e.target.value);
                  }}
                  className="select-compact"
                >
                  <option value="en">{i18n.t('langEN')}</option>
                  <option value="de">{i18n.t('langDE')}</option>
                </select>
              </label>
            </div>
            {/* <div className="field">
              <label className="switch">
                <input
                  type="checkbox"
                  id="settings-auto-sync"
                  checked={autoSync}
                  onChange={(e) => {
                    setAutoSync(e.target.checked);
                  }}
                />
                <span className="slider"></span>
              </label>
              <span className="switch-label">{i18n.t('autoSync')}</span>
            </div> */}
            <div className="field">
              <label className="switch">
                <input
                  type="checkbox"
                  id="settings-light-mode"
                  checked={theme === 'light'}
                  onChange={(e) => {
                    setTheme(e.target.checked ? 'light' : 'dark');
                  }}
                />
                <span className="slider"></span>
              </label>
              <span className="switch-label">{i18n.t('lightMode')}</span>
            </div>
            <div className="row">
              <button
                id="sign-out"
                className="btn"
                onClick={handleSignOutClick}
              >
                {i18n.t('signOut')}
              </button>
            </div>
          </div>
        </div>

        <div className="card settings-data">
          <h2>{i18n.t('dataManagement')}</h2>
          <div className="row">
            <button
              id="export-json"
              className="btn"
              onClick={handleExportJson}
            >
              {i18n.t('exportJson')}
            </button>
            <label className="btn" id="import-label">
              <span>{i18n.t('importJson')}</span>
              <input
                id="import-json"
                type="file"
                accept="application/json"
                className="hidden"
                onChange={handleImportJson}
              />
            </label>
          </div>
        </div>
      </div>

      {/* Global ConfirmDialog for sign out action */}
      <ConfirmDialog
        open={showConfirmSignOut}
        title={i18n?.t ? i18n.t('confirm') : 'Confirm'}
        message={i18n?.t ? i18n.t('signOutConfirm') : 'Are you sure you want to sign out?'}
        confirmLabel={i18n?.t ? i18n.t('signOut') : 'Sign out'}
        cancelLabel={i18n?.t ? i18n.t('cancel') : 'Cancel'}
        onConfirm={handleConfirmSignOut}
        onCancel={handleCancelSignOut}
        confirmType="danger"
      />
    </section>
  );
};

export default SettingsView;